import {IUploadFileFormBaseValues, IUploadScormFormBaseValues, IAddWebLinkFormBaseValues} from '../schema';
import {ILookup} from 'models/common/ILookup';
import {ResourceTypeNumEnum} from 'admin/resources/enums';
import {
  emptyResourceMetadataLookupNames,
  IResourceMetadataLookupNames,
  toNumberIds,
  toNumberLookup,
  toNumberLookups,
  toOptionalBoolean,
  toOptionalNumber,
  toStringLookups,
} from 'admin/resources/utils';

const toNumberLookupIdOnly = (id?: number | string | null): ILookup<number> | undefined => {
  const parsed = toOptionalNumber(id);
  return parsed === undefined ? undefined : {id: parsed, name: ''};
};

const mapBaseResourceMetadata = (
  values: IUploadFileFormBaseValues | IUploadScormFormBaseValues | IAddWebLinkFormBaseValues
) => ({
  isAccessibleToStudents: toOptionalBoolean(values.isAccessibleToStudents),
  keyTerms: values.keyTerms,
  learningObjectives: values.learningObjectives,
  languages: toNumberIds(values.languages),
  accessExpirationDate: values.expirationDate,
  competencies: toNumberIds(values.competencies),
  pillars: toNumberIds(values.pillars),
  programs: toNumberIds(values.programs),
  gradeLevels: toNumberIds(values.gradeLevels),
  kSAs: toNumberIds(values.knowledgeSkillsAndAbilities),
  lengthInMinutes: 0,
  programYear: '',
});

const mapImageMetadata = (values: IUploadFileFormBaseValues, lookupNames: IResourceMetadataLookupNames) => ({
  stockImageLinkNumber: values.stockImageLinkNumber ?? '',
  imageSources: toNumberLookups(values.imageSources, lookupNames.imageSources),
  ethnicities: toNumberLookups(values.ethnicities, lookupNames.ethnicities),
  genders: toNumberLookups(values.genders, lookupNames.genders),
  publishedOutputTool: toNumberLookup(values.publishedOutputTool, lookupNames.publishedOutputTools),
  alternateText: values.alternateText ?? '',
  linkToStockImageSource: values.linkToStockImageSource ?? '',
});

const mapExtendedFileMetadata = (values: IUploadFileFormBaseValues, lookupNames: IResourceMetadataLookupNames) => {
  const associatedResources = toStringLookups(values.associatedResources, lookupNames.associatedResources);
  const explicitResourceIds = toStringLookups(
    values.resourceIds?.length ? values.resourceIds : [],
    lookupNames.associatedResources
  ).map((lookup) => lookup.id);
  const resourceIds = explicitResourceIds.length ? explicitResourceIds : associatedResources.map((lookup) => lookup.id);
  const numberOfResources = toOptionalNumber(values.numberOfResources);
  const isImageFileType = values.fileType === ResourceTypeNumEnum.Image;

  return {
    permissionNeeded: toOptionalBoolean(values.permissionNeeded),
    ...(isImageFileType ? {imageMetadata: mapImageMetadata(values, lookupNames)} : {}),
    slideNumber: toOptionalNumber(values.slideNumber),
    sessionThemeNumber: toOptionalNumber(values.sessionThemeNumber),
    associatedResources,
    fileName: values.fileName ?? values.file?.name ?? '',
    link: values.link ?? '',
    mediaRights: {
      urlSource: values.urlSource ?? '',
      usageType: toNumberLookupIdOnly(values.usageType),
      reuseFlag: toOptionalBoolean(values.reuseFlag),
      notes: values.notes ?? '',
      permissionAgreementUrl: values.permissionAgreementUrl ?? '',
    },
    numberOfResources: numberOfResources ?? associatedResources.length,
    resourceIds,
    characterTraits: toNumberLookups(values.characterTraits, lookupNames.characterTraits),
    behavioralIndicators: toNumberLookups(values.behavioralIndicators, lookupNames.behavioralIndicators),
    videoTranscriptionLink: values.videoTranscriptionLink ?? '',
    credentialing: toOptionalBoolean(values.credentialing),
    permissionInPerpetuity: values.permissionInPerpetuity ?? false,
    accessibilityStatus: toNumberLookup(values.accessibilityStatus, lookupNames.accessibilityStatuses) ?? null,
    accessibilityReports: values.accessibilityReports ?? '',
  };
};

export const mapUploadResourceMetadata = (
  values: IUploadFileFormBaseValues | IUploadScormFormBaseValues | IAddWebLinkFormBaseValues,
  lookupNames: IResourceMetadataLookupNames = emptyResourceMetadataLookupNames()
) => {
  const baseMetadata = mapBaseResourceMetadata(values);

  // File upload form includes these keys in initialValues
  if ('permissionNeeded' in values) {
    return {
      ...baseMetadata,
      ...mapExtendedFileMetadata(values as IUploadFileFormBaseValues, lookupNames),
    };
  }

  return baseMetadata;
};
